import { useCallback, useEffect } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import {
  calculateCenterOfPoints,
  calculateCursorPosition,
  calculateSelectionRect,
  isPointInSelectionRect,
} from './mouseEventUtils';
import { useDragManager } from '../../components/svgElements/DragManager';
import { Position2D, SelectedPoint } from '../types/eventTypes';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import { Point } from '../../types/Point';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';

/**
 * Properties for useMouseMoveEvents hook
 */
export interface MouseMoveEventProps {
  /** Reference to SVG element */
  svgRef: React.RefObject<SVGSVGElement>;

  /** Reference to current cursor position */
  cursorPositionRef: React.MutableRefObject<Position2D>;

  /** Function to update cursor position in store */
  setCursorPosition: (position: Position2D) => void;

  /** Whether user is currently drag-selecting */
  isDragSelecting: boolean;

  /** Position where drag selection started */
  dragStartPosition: Position2D;

  /** Currently selected points */
  selectedPoints: SelectedPoint[];

  /** Function to update selected points locally */
  setSelectedPointsLocal: (points: SelectedPoint[]) => void;

  /** Whether paste preview is currently showing */
  showPastePreview: boolean;

  /** Reference to points being previewed for paste */
  pastePreviewPoints: React.MutableRefObject<Point[]>;

  /** Function to update paste preview offset */
  setPastePreviewOffset: (offset: Position2D) => void;

  /** Reference to position where paste operation should occur */
  pastePositionRef: React.MutableRefObject<Position2D | null>;

  /** List of all assemblies that can be interacted with */
  assembliesList: TakeoffAssembly[];

  /** Objects that have been copied and can be pasted */
  copiedObjects: any[];
}

/**
 * Custom hook for handling mouse movement events in the SVG editor
 */
export const useMouseMoveEvents = ({
  svgRef,
  cursorPositionRef,
  setCursorPosition,
  isDragSelecting,
  dragStartPosition,
  selectedPoints,
  setSelectedPointsLocal,
  showPastePreview,
  pastePreviewPoints,
  setPastePreviewOffset,
  pastePositionRef,
  assembliesList,
  copiedObjects,
}: MouseMoveEventProps) => {
  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
  const { currentTakeoffAssembly } = useStore(useAssemblyStore);
  const { isLassoMode, isLassoDrawing, setIsLassoDrawing, lassoPoints, setLassoPoints } =
    useStore(useUnityBuildStore);
  const { setSelectedPoints, setHasCopiedItems } = useStore(useSelectionStore);
  const { isDragging: isDraggingPoints } = useDragManager();
  const { groupDragState } = useStore(useSelectionStore);

  /**
   * Updates the paste preview position based on cursor or fixed position
   */
  const updatePastePreviewPosition = useCallback(
    (cursorPos: Position2D) => {
      if (!showPastePreview || pastePreviewPoints.current.length === 0) return;

      // Only use pastePositionRef if it's explicitly set (frozen position)
      // Otherwise, ALWAYS follow the cursor
      const previewPosition = pastePositionRef.current || cursorPos;

      const center = calculateCenterOfPoints(pastePreviewPoints.current);
      const offset = {
        x: previewPosition.x - center.x,
        y: previewPosition.y - center.y,
      };
      setPastePreviewOffset(offset);

      // Store the offset globally so PointEditor can access it
      (window as any).__currentPastePreviewOffset = offset;
    },
    [showPastePreview, pastePreviewPoints, pastePositionRef, setPastePreviewOffset]
  );

  /**
   * Updates the selection based on the current drag rectangle
   */
  const updateDragSelection = useCallback(
    (cursorPos: Position2D) => {
      const selectionStore = useSelectionStore.getState();

      // Skip if we're not in drag selection mode or if other operations are active
      if (
        !isDragSelecting ||
        currentTakeoffAssembly ||
        useKeyBoardEventStore.getState().isMeasuring ||
        useUnityBuildStore.getState().isDragging || //<--- Prevent drag selection when a point is being dragged
        selectionStore.groupDragState.isDraggingGroup //<--- Prevent drag selection when group dragging
      ) {
        return;
      }

      // Create array for points that fall within selection rectangle
      const updatedPoints: SelectedPoint[] = [];

      // Calculate selection rectangle once to improve performance
      const selectionRect = calculateSelectionRect(dragStartPosition, cursorPos);
      // Broadcast selection rectangle for other systems (e.g., annotations) to react in parallel
      try {
        document.dispatchEvent(
          new CustomEvent('annotations:drag-select-rect', { detail: { rect: selectionRect } })
        );
      } catch {}

      if (assembliesList?.length > 0) {
        // Loop through all assemblies and their points
        assembliesList.forEach(assembly => {
          if (!assembly.pointsList) return;

          assembly.pointsList.forEach((point: Point) => {
            // Check if point falls within the selection rectangle
            if (isPointInSelectionRect(point, selectionRect)) {
              updatedPoints.push({
                pointId: point.pointid,
                assemblyId: assembly.assemblyid,
                originalX: point.x,
                originalY: point.y,
              });
            }
          });
        });
      }

      // Only update the state if the selection has changed
      const selectionChanged =
        updatedPoints.length !== selectedPoints.length ||
        !selectedPoints.every(sp => updatedPoints.some(up => up.pointId === sp.pointId));

      if (selectionChanged) {
        // Update both local and global selection states
        setSelectedPointsLocal(updatedPoints);

        // Always update the global selection store for consistent behavior
        setSelectedPoints(updatedPoints);

        // Update the hasCopiedItems flag to enable copy operations
        setHasCopiedItems(copiedObjects.length > 0);

        // Also highlight the selected points visually in the SVG
        if (updatedPoints.length > 0) {
          // Create a map of selected point IDs for faster lookup
          const selectedPointIdSet = new Set(updatedPoints.map(p => p.pointId));

          // Update the assemblies list with visual selection indicators
          const updatedAssembliesList = assembliesList.map(assembly => {
            if (!assembly.pointsList) return assembly;

            const updatedPointsList = assembly.pointsList.map(point => ({
              ...point,
              // Set isselected flag based on whether the point is in our selection
              isselected: selectedPointIdSet.has(point.pointid),
            }));

            return {
              ...assembly,
              pointsList: updatedPointsList,
            };
          });

          // Update the store with the visually updated assemblies
          // This is safe as we're not changing structure, only visual properties
          useUnityBuildStore.setState(state => ({
            ...state,
            filteredCountedAssemblies: Array.isArray(filteredCountedAssemblies)
              ? []
              : [
                  {
                    ...filteredCountedAssemblies?.filteredAssemblies?.[0],
                    assembliesList: updatedAssembliesList,
                  },
                ],
          }));
        }
      }
    },
    [
      isDragSelecting,
      dragStartPosition,
      assembliesList,
      selectedPoints,
      setSelectedPointsLocal,
      setSelectedPoints,
      setHasCopiedItems,
      copiedObjects.length,
      currentTakeoffAssembly,
      filteredCountedAssemblies,
    ]
  );

  /**
   * Main handler for mouse movement events
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      // Check if the user is currently focused on a text input element
      const target = e.target as HTMLElement;
      const activeElement = document.activeElement;

      // Don't interfere with text editing (but still allow position tracking for other purposes)
      const isEditingText =
        activeElement &&
        (activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'INPUT' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'INPUT' ||
          target.getAttribute('contenteditable') === 'true');

      // Calculate and update cursor position
      const position = calculateCursorPosition(e, svgRef);
      if (!position) return;

      // Update cursor position in ref and store
      cursorPositionRef.current = position;
      setCursorPosition(position);

      // Handle paste preview updates (but not when editing text)
      if (!isEditingText) {
        updatePastePreviewPosition(position);
      }

      // Lasso mode handling - but not during point dragging, text editing, or when selected points could be dragged
      if (isLassoMode && !isDraggingPoints && !groupDragState.isDraggingGroup && !isEditingText) {
        // Start drawing when left button down
        if (!isLassoDrawing) {
          if ((e as MouseEvent).buttons === 1) {
            setIsLassoDrawing(true);
            setLassoPoints([{ x: position.x, y: position.y }]);
          }
          return;
        }
        // Append during draw
        setLassoPoints(prev => [...prev, { x: position.x, y: position.y }]);
        return;
      }

      // Handle drag selection updates (but not when editing text)
      if (!isEditingText) {
        updateDragSelection(position);
      }
    },
    [
      svgRef,
      cursorPositionRef,
      setCursorPosition,
      updatePastePreviewPosition,
      updateDragSelection,
      isLassoMode,
      isLassoDrawing,
      setIsLassoDrawing,
      setLassoPoints,
      isDraggingPoints,
      groupDragState,
    ]
  );

  // Point-in-polygon helper
  const isPointInPolygon = useCallback((x: number, y: number, poly: { x: number; y: number }[]) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x,
        yi = poly[i].y;
      const xj = poly[j].x,
        yj = poly[j].y;
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }, []);

  // Finalize selection when lasso completes (inside-polygon rule)
  useEffect(() => {
    if (!isLassoMode || isLassoDrawing || lassoPoints.length < 3) return;

    const poly = lassoPoints;
    const updated: SelectedPoint[] = [];
    assembliesList.forEach(assembly => {
      assembly.pointsList?.forEach((p: Point) => {
        if (isPointInPolygon(p.x, p.y, poly)) {
          updated.push({
            pointId: p.pointid,
            assemblyId: assembly.assemblyid,
            originalX: p.x,
            originalY: p.y,
          });
        }
      });
    });

    setSelectedPointsLocal(updated);
    setSelectedPoints(updated);
    setHasCopiedItems(false);
    // Clear lasso path to avoid re-triggering finalize
    setLassoPoints([]);
  }, [
    isLassoMode,
    isLassoDrawing,
    lassoPoints,
    assembliesList,
    setSelectedPointsLocal,
    setSelectedPoints,
    setHasCopiedItems,
    isPointInPolygon,
    setLassoPoints,
  ]);

  return { handleMouseMove };
};
